/**
 * The detail panel's field catalogue for #472.
 *
 * Labels live here rather than in the backend for the same reason the table's
 * KOLONNER do: the API speaks field names, the UI speaks Norwegian. Keeping
 * both lists on this side means adding a field is one edit, and the export and
 * the panel can never drift out of sync — they read the same catalogue.
 *
 * Order within a group is the order it renders, which is deliberate: the
 * fields a dealer reads first come first, not the order SVV happens to nest
 * them in.
 */
import type { ParseKeys } from 'i18next'
import i18n from '../../i18n'
import { formatNorwegianDate } from '../../utils/dateUtils'
import type { Detaljer } from './oppslagApi'

/** ISO date or ISO timestamp, both of which SVV returns. */
const ISO_DATO = /^\d{4}-\d{2}-\d{2}/

export type OppslagKey = ParseKeys<'oppslag'>

/** Translated at call time, so a language switch reaches the next render. */
const tr = (key: OppslagKey, values?: Record<string, unknown>) =>
  i18n.t(key, { ns: 'oppslag', ...values }) as string

export interface Felt {
  key: string
  labelKey: OppslagKey
  enhet?: string
}

export interface Gruppe {
  key: keyof Detaljer
  tittelKey: OppslagKey
  felter: Felt[]
}

export const GRUPPER: Gruppe[] = [
  {
    key: 'registrering',
    tittelKey: 'detaljer.grupper.registrering',
    felter: [
      // Abroad for a used import, so it can predate the Norway date (#1000).
      { key: 'registrert_forstegang', labelKey: 'detaljer.felter.registrertForstegang' },
      { key: 'registrert_forstegang_norge', labelKey: 'detaljer.felter.registrertForstegangNorge' },
      { key: 'registrert_fra', labelKey: 'detaljer.felter.registrertFra' },
      { key: 'registrert_forstegang_pa_eier', labelKey: 'detaljer.felter.registrertForstegangPaEier' },
      { key: 'registreringsstatus', labelKey: 'detaljer.felter.registreringsstatus' },
      { key: 'kjoringens_art', labelKey: 'detaljer.felter.kjoringensArt' },
    ],
  },
  {
    key: 'motor',
    tittelKey: 'detaljer.grupper.motor',
    felter: [
      { key: 'arbeidsprinsipp', labelKey: 'detaljer.felter.arbeidsprinsipp' },
      { key: 'effekt_hk', labelKey: 'detaljer.felter.effekt', enhet: 'hk' },
      { key: 'effekt_kw', labelKey: 'detaljer.felter.effekt', enhet: 'kW' },
      // Continuous rating rather than peak — the figure that differs most
      // between an electric motor's headline power and what it sustains.
      { key: 'maks_effekt_pr_time', labelKey: 'detaljer.felter.maksEffektPrTime', enhet: 'kW' },
      { key: 'slagvolum', labelKey: 'detaljer.felter.slagvolum', enhet: 'cm³' },
      { key: 'antall_sylindre', labelKey: 'detaljer.felter.antallSylindre' },
      { key: 'sylinderarrangement', labelKey: 'detaljer.felter.sylinderarrangement' },
      { key: 'girkasse', labelKey: 'detaljer.felter.girkasse' },
      { key: 'hybridkategori', labelKey: 'detaljer.felter.hybridkategori' },
      { key: 'toppfart', labelKey: 'detaljer.felter.toppfart', enhet: 'km/t' },
      { key: 'motorkode', labelKey: 'detaljer.felter.motorkode' },
      { key: 'partikkelfilter_motor', labelKey: 'detaljer.felter.partikkelfilterMotor' },
    ],
  },
  {
    key: 'miljo',
    tittelKey: 'detaljer.grupper.miljo',
    felter: [
      { key: 'drivstoff', labelKey: 'detaljer.felter.drivstoff' },
      { key: 'euro_klasse', labelKey: 'detaljer.felter.euroKlasse' },
      { key: 'co2_blandet', labelKey: 'detaljer.felter.co2Blandet', enhet: 'g/km' },
      { key: 'forbruk_blandet', labelKey: 'detaljer.felter.forbrukBlandet', enhet: 'l/100 km' },
      { key: 'rekkevidde_km', labelKey: 'detaljer.felter.rekkevidde', enhet: 'km' },
      // Says whether the two figures above are comparable with another car's.
      { key: 'malemetode', labelKey: 'detaljer.felter.malemetode' },
      { key: 'utslipp_nox_mg_km', labelKey: 'detaljer.felter.utslippNox', enhet: 'mg/km' },
      { key: 'utslipp_partikler_mg_km', labelKey: 'detaljer.felter.utslippPartikler', enhet: 'mg/km' },
      { key: 'partikkelfilter_fabrikkmontert', labelKey: 'detaljer.felter.partikkelfilterFabrikkmontert' },
    ],
  },
  {
    key: 'vekter',
    tittelKey: 'detaljer.grupper.vekter',
    felter: [
      { key: 'egenvekt', labelKey: 'detaljer.felter.egenvekt', enhet: 'kg' },
      { key: 'egenvekt_minimum', labelKey: 'detaljer.felter.egenvektMinimum', enhet: 'kg' },
      { key: 'nyttelast', labelKey: 'detaljer.felter.nyttelast', enhet: 'kg' },
      { key: 'tillatt_totalvekt', labelKey: 'detaljer.felter.tillattTotalvekt', enhet: 'kg' },
      { key: 'tillatt_tilhengervekt_med_brems', labelKey: 'detaljer.felter.tilhengervektMedBrems', enhet: 'kg' },
      { key: 'tillatt_tilhengervekt_uten_brems', labelKey: 'detaljer.felter.tilhengervektUtenBrems', enhet: 'kg' },
      { key: 'tillatt_vogntogvekt', labelKey: 'detaljer.felter.tillattVogntogvekt', enhet: 'kg' },
      { key: 'tillatt_vertikal_koplingslast', labelKey: 'detaljer.felter.vertikalKoplingslast', enhet: 'kg' },
    ],
  },
  {
    key: 'karosseri',
    tittelKey: 'detaljer.grupper.karosseri',
    felter: [
      { key: 'karosseritype', labelKey: 'detaljer.felter.karosseritype' },
      { key: 'farge', labelKey: 'detaljer.felter.farge' },
      { key: 'antall_dorer', labelKey: 'detaljer.felter.antallDorer' },
      { key: 'plassering_dorer', labelKey: 'detaljer.felter.plasseringDorer' },
      { key: 'sitteplasser_totalt', labelKey: 'detaljer.felter.sitteplasserTotalt' },
      { key: 'sitteplasser_foran', labelKey: 'detaljer.felter.sitteplasserForan' },
      { key: 'lengde', labelKey: 'detaljer.felter.lengde', enhet: 'mm' },
      { key: 'bredde', labelKey: 'detaljer.felter.bredde', enhet: 'mm' },
      { key: 'hoyde', labelKey: 'detaljer.felter.hoyde', enhet: 'mm' },
      { key: 'akselavstand', labelKey: 'detaljer.felter.akselavstand', enhet: 'mm' },
      { key: 'antall_aksler', labelKey: 'detaljer.felter.antallAksler' },
      { key: 'kjoreside', labelKey: 'detaljer.felter.kjoreside' },
      { key: 'abs', labelKey: 'detaljer.felter.abs' },
    ],
  },
  {
    key: 'godkjenning',
    tittelKey: 'detaljer.grupper.godkjenning',
    felter: [
      { key: 'kjoretoyklassifisering', labelKey: 'detaljer.felter.kjoretoyklassifisering' },
      { key: 'teknisk_kode', labelKey: 'detaljer.felter.tekniskKode' },
      { key: 'avgiftskode', labelKey: 'detaljer.felter.avgiftskode' },
      { key: 'godkjenningstype', labelKey: 'detaljer.felter.godkjenningstype' },
      { key: 'gyldig_fra', labelKey: 'detaljer.felter.gyldigFra' },
      { key: 'typegodkjenningsnummer', labelKey: 'detaljer.felter.typegodkjenningsnummer' },
      { key: 'variant', labelKey: 'detaljer.felter.variant' },
      { key: 'versjon', labelKey: 'detaljer.felter.versjon' },
      { key: 'i_samsvar_med_typegodkjenning', labelKey: 'detaljer.felter.iSamsvarMedTypegodkjenning' },
    ],
  },
]

/** A value SVV did not have for this vehicle. Distinct from `false`, which is
 *  a real answer — so this cannot be a plain falsy check. */
export function mangler(verdi: unknown): boolean {
  return verdi === null || verdi === undefined || verdi === ''
}

export function formater(verdi: unknown, enhet?: string): string {
  if (mangler(verdi)) return '—'
  if (typeof verdi === 'boolean') return verdi ? tr('detaljer.ja') : tr('detaljer.nei')
  // SVV returns ISO; the app shows dd.MM.yyyy everywhere else.
  if (typeof verdi === 'string' && ISO_DATO.test(verdi)) {
    return formatNorwegianDate(verdi)
  }
  return enhet ? `${verdi} ${enhet}` : String(verdi)
}

export interface SpesifikasjonsRad {
  gruppe: string
  felt: string
  verdi: string
}

/**
 * One vehicle's full specification as rows, for export.
 *
 * Rows rather than columns on purpose. Sixty technical fields across the top
 * of a PDF is unreadable; the same sixty down the side is a spec sheet. The
 * table export above it stays column-shaped, because that one is a list of
 * vehicles.
 *
 * Fields SVV had no value for are omitted — a spec sheet of dashes tells a
 * customer nothing, and their absence is already visible in the panel.
 */
export function tilSpesifikasjonsRader(detaljer: Detaljer): SpesifikasjonsRad[] {
  const rader: SpesifikasjonsRad[] = []

  for (const gruppe of GRUPPER) {
    const innhold = detaljer[gruppe.key] as Record<string, unknown> | undefined
    if (!innhold) continue
    for (const felt of gruppe.felter) {
      const verdi = innhold[felt.key]
      if (mangler(verdi)) continue
      const label = tr(felt.labelKey)
      rader.push({
        gruppe: tr(gruppe.tittelKey),
        felt: felt.enhet ? `${label} (${felt.enhet})` : label,
        verdi: formater(verdi),
      })
    }
  }

  for (const dekk of detaljer.dekk_og_felg ?? []) {
    rader.push({
      gruppe: tr('detaljer.dekkOgFelg'),
      felt: tr('detaljer.dekk.rad', { kombinasjon: dekk.kombinasjon, aksel: dekk.aksel ?? '—' }),
      verdi: [
        dekk.dekkdimensjon,
        dekk.felgdimensjon,
        dekk.belastningskode && tr('detaljer.dekk.last', { kode: dekk.belastningskode }),
        dekk.hastighetskode && tr('detaljer.dekk.hastighetVerdi', { kode: dekk.hastighetskode }),
        dekk.innpress && tr('detaljer.dekk.innpressVerdi', { verdi: dekk.innpress }),
      ]
        .filter(Boolean)
        .join(', '),
    })
  }

  for (const merknad of detaljer.merknader ?? []) {
    rader.push({ gruppe: tr('detaljer.merknader'), felt: tr('detaljer.merknad'), verdi: merknad })
  }

  return rader
}

/** A function rather than a constant so the labels follow the language. */
export function spesifikasjonKolonner() {
  return [
    { key: 'gruppe', label: tr('detaljer.spesifikasjonKolonner.gruppe') },
    { key: 'felt', label: tr('detaljer.spesifikasjonKolonner.felt') },
    { key: 'verdi', label: tr('detaljer.spesifikasjonKolonner.verdi') },
  ]
}
